// src/components/NotificationDropdown.js
import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Button,
  Badge
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNavigate } from 'react-router-dom';
import api, { subscribeEvents } from '../services/api';

const NotificationDropdown = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const navigate = useNavigate();
  const restAbortControllerRef = useRef();

  useEffect(() => {
    // 1. Initial REST fetch
    (async () => {
      const controller = new AbortController();
      restAbortControllerRef.current = controller;
      try {
        const userId = localStorage.getItem('userId');
        const res = await api.get(`/api/notifications/${userId}`, {
          signal: controller.signal
        });
        setNotifications(res.data || []);
      } catch (err) {
        if (err.name !== 'CanceledError') {
          console.error(err);
        }
      }
    })();

    // 2. SSE subscribe (auto-refresh on 401)
    const sseController = subscribeEvents('/api/notifications', {
      onmessage: event => {
        const newNotification = JSON.parse(event.data);
        setNotifications(prev => [newNotification, ...prev]);
      },
      onerror: err => {
        console.error('SSE Error:', err);
      }
    });

    return () => {
      restAbortControllerRef.current?.abort();
      sseController.abort();
    };
  }, []);

  const handleClick = e => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleNotificationClick = async notif => {
    try {
      await api.delete(`/api/notifications/${notif.deviceName}`);
      setNotifications(prev => prev.filter(n => n.deviceName !== notif.deviceName));
      navigate(`/water-meters/${notif.deviceName}`, {
        state: { openShutdown: notif.event === 'shutdown' }
      });
    } catch (err) {
      console.error(err);
    } finally {
      handleClose();
    }
  };

  const handleClearAll = async () => {
    try {
      await api.delete('/api/notifications/user');
      setNotifications([]);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Box>
      <IconButton color="inherit" onClick={handleClick}>
        <Badge badgeContent={notifications.length} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        TransitionProps={{ timeout: 300 }}
      >
        {notifications.length === 0 ? (
          <MenuItem>
            <Typography variant="body2">No notifications</Typography>
          </MenuItem>
        ) : (
          notifications.map((notif, idx) => (
            <MenuItem key={idx} onClick={() => handleNotificationClick(notif)}>
              <Typography variant="body2">
                {notif.deviceName} {notif.event} {notif.status}
              </Typography>
            </MenuItem>
          ))
        )}
        {notifications.length > 0 && (
          <Box sx={{ textAlign: 'center', p: 1 }}>
            <Button variant="contained" size="small" onClick={handleClearAll}>
              Clear All
            </Button>
          </Box>
        )}
      </Menu>
    </Box>
  );
};

export default NotificationDropdown;
